import { useMemo } from "react";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { SmartViewsCollection } from "@/domains/tanstack-db/core/collections";
import { TanstackBaseRepository, TanstackRepositoryArgs } from "../base/repository";
import { SMART_VIEW_ORDER_BY_OPTIONS } from "@/domains/tanstack-db/common/constants";
import { smartViewRecordSchema, smartViewUpsertSchema } from "./schemas";
import { SmartViewRow, SmartViewUpsertInput } from "./schemas";

export class SmartViewRepository extends TanstackBaseRepository<SmartViewsCollection> {
  constructor({ collection }: TanstackRepositoryArgs<SmartViewsCollection>) {
    super({
      collection,
    });
  }

  async upsertView(params: SmartViewUpsertInput): Promise<void> {
    const input = smartViewUpsertSchema.parse(params);
    const existing = this.collection.get(input.id) ?? null;
    const nowIso = new Date().toISOString();

    const record = smartViewRecordSchema.parse({
      id: input.id,
      associated_space_account_id: input.associated_space_account_id,
      smart_context_id: input.smart_context_id,
      derived_kind: input.derived_kind ?? existing?.derived_kind ?? null,
      derived_internal_label:
        input.derived_internal_label ?? existing?.derived_internal_label ?? null,
      value_json:
        input.value_json !== undefined ? input.value_json : (existing?.value_json ?? null),
      has_found_more: input.has_found_more ?? existing?.has_found_more ?? false,
      refreshed_at: input.refreshed_at ?? existing?.refreshed_at ?? null,
      created_at: input.created_at ?? existing?.created_at ?? nowIso,
      updated_at: input.updated_at ?? nowIso,
    });

    if (existing) {
      const tx = this.collection.update(record.id, draft => {
        Object.assign(draft, record);
      });
      await tx.isPersisted.promise;
      return;
    }

    const tx = this.collection.insert(record);
    await tx.isPersisted.promise;
  }

  useLatestByContextId({ smartContextId }: { smartContextId: string | null }): {
    view: SmartViewRow | null;
    isLoading: boolean;
    error: string | null;
  } {
    const isEnabled = Boolean(smartContextId);

    const queryResult = useLiveQuery(
      q => {
        if (!isEnabled) return undefined;

        return q
          .from({ smartViews: this.collection })
          .where(({ smartViews }) => eq(smartViews.smart_context_id, smartContextId))
          .orderBy(({ smartViews }) => smartViews.refreshed_at, SMART_VIEW_ORDER_BY_OPTIONS)
          .orderBy(({ smartViews }) => smartViews.updated_at, SMART_VIEW_ORDER_BY_OPTIONS)
          .orderBy(({ smartViews }) => smartViews.created_at, SMART_VIEW_ORDER_BY_OPTIONS)
          .limit(1);
      },
      [smartContextId]
    );

    const view = useMemo(() => {
      if (!isEnabled) return null;
      const rows = queryResult.data ?? [];
      return rows[0] ?? null;
    }, [isEnabled, queryResult.data]);

    const error = isEnabled && queryResult.status === "error" ? "Failed to load smart view." : null;

    return {
      view,
      isLoading: isEnabled ? queryResult.isLoading : false,
      error,
    };
  }

  useById({ smartViewId }: { smartViewId: string | null }): {
    view: SmartViewRow | null;
    isLoading: boolean;
    error: string | null;
  } {
    const isEnabled = Boolean(smartViewId);

    const queryResult = useLiveQuery(
      q => {
        if (!isEnabled) return undefined;

        return q
          .from({ smartViews: this.collection })
          .where(({ smartViews }) => eq(smartViews.id, smartViewId))
          .orderBy(({ smartViews }) => smartViews.updated_at, SMART_VIEW_ORDER_BY_OPTIONS)
          .findOne();
      },
      [smartViewId]
    );

    const view = queryResult.data ?? null;
    const error = isEnabled && queryResult.status === "error" ? "Failed to load smart view." : null;

    return {
      view: isEnabled ? view : null,
      isLoading: isEnabled ? queryResult.isLoading : false,
      error,
    };
  }
}
